import { Box, Container, Typography, Card, CardContent, Button, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import PublicFooter from './PublicFooter';

/**
 * The school's public landing card at the bare `/:schoolSlug`.
 *
 * Since 2026-09-16 this is the whole landing page (the website builder that used
 * to render a published home page here was removed), so it must handle the two
 * tenant states the old layout handled for it: still loading (no name yet — an
 * empty card with three live CTAs is a flash of broken UI), and an unknown slug
 * (a nameless card inviting a booking with a school that does not exist).
 */
export default function SchoolProfileFallback() {
  const { school, schoolSlug, loading, error } = useTenant();

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !school) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#FCF6E6', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ py: 6, flex: 1, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            School not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error || 'There is no school at this address.'}
          </Typography>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FCF6E6', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ py: 6, flex: 1 }}>
        {/* School logo */}
        {school?.branding?.logoUrl && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={school.branding.logoUrl}
              alt={school.name}
              sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}

        <Card elevation={0} sx={{ borderRadius: 3, p: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {school?.name}
            </Typography>
            {school?.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {school.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to={`/${schoolSlug}/meet-and-greet`}
                variant="contained"
                sx={{ textTransform: 'none' }}
              >
                Book a Meet & Greet
              </Button>
              <Button
                component={Link}
                to={`/${schoolSlug}/enquiry`}
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                Send an Enquiry
              </Button>
              <Button
                component={Link}
                to={`/${schoolSlug}/login`}
                variant="text"
                sx={{ textTransform: 'none' }}
              >
                Login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <PublicFooter schoolName={school?.name} schoolSlug={schoolSlug || undefined} />
    </Box>
  );
}
